import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { ISimpleTab } from '~/src/dash/components/tabs/common';
import { SimpleTabs } from '~/src/dash/components/tabs/SimpleTabs';
import { SimpleTabSections } from '~/src/dash/components/tabs/SimpleTabSections';
import { Panel, PanelBreak, Panels, spacing } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { routeName } from '~/src/dash/utils/routes';
import { useBaseConfig, useBaseStore } from '~/src/dash/views/base/contexts';
import { fetchChannelMappings } from '~/src/dash/views/channels/api';
import { ChannelGroupsContainer } from '~/src/dash/views/channels/ChannelGroups/container';
import { ChannelMappingsContainer } from '~/src/dash/views/channels/ChannelMappings/container';
import { ChannelsContainer } from '~/src/dash/views/channels/Channels/container';
import { IChannelsConfig } from '~/src/dash/views/channels/config';
import { ChannelsContext } from '~/src/dash/views/channels/contexts';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

export const ChannelsView = () => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [key, setKey] = useState(0);
  const [tabs, setTabs] = useState<Array<ISimpleTab>>([]);
  const [tabSections, setTabSections] = useState<(() => JSX.Element)[]>([]);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [error, setError] = useState<any>(null);

  const { classes, location, session, user } = useBaseStore();
  const { Channels } = useBaseConfig();
  const cfg = Channels.Main as IChannelsConfig;
  const { pathname } = location;

  // when user clicks on a tab
  const handleChangeTab = (_event: React.SyntheticEvent, index: number) => {
    const tabKey = cfg.tabs.find((tab) => tab.value === index)?.key;
    track(`Channels - View ${tabKey} Tab`, { isSuperUser: hasRoleSuperUser(user.user) }, user.user);
    setActiveTabIndex(index);
  };

  const forceUpdate = (tabIndex: number) => {
    unstable_batchedUpdates(() => {
      setActiveTabIndex(tabIndex);
      setKey((value) => value + 1);
    });
  };

  // we need to fetch ChannelMappings first to determine whether to display the Mappings tab
  const onFetchChannelMappings = () => {
    return fetchChannelMappings(session.publisher.ID)
      .then(({ TotalRows }) => {
        if (TotalRows > 0) {
          setTabSections([ChannelsContainer, ChannelGroupsContainer, ChannelMappingsContainer]);
          setTabs(cfg.tabs);
        } else {
          setTabSections([ChannelsContainer, ChannelGroupsContainer]);
          setTabs(cfg.tabs.filter(({ key }) => key !== 'Mappings'));
        }
      })
      .catch((err) => {
        setError(err);
        setSnackbarMessage('Error retrieving some data. Please reload');
      });
  };

  useEffect(() => {
    onFetchChannelMappings();
  }, []);

  return (
    <ChannelsContext.Provider
      value={{
        cfg,
        readAuthorization: IS_AUTHORIZED(['read_channel'], user.user),
        writeAuthorization: IS_AUTHORIZED(['write_channel'], user.user),
        forceUpdate,
      }}
    >
      <Panels max vertical relative minWidthMain>
        <Panel
          largeHorizontalPadding
          verticalPadding
          style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
        >
          <ModelTopBar
            disableTitlePaddingLeft
            classes={classes}
            currentPath={pathname}
            publisher={session.publisher}
            session={session}
            title={routeName(pathname)}
            user={user}
          />
        </Panel>
        <Panel>
          <SimpleTabs
            activeTabIndex={activeTabIndex}
            ariaLabel='Channels'
            handleChangeTab={handleChangeTab}
            tabs={tabs}
          />
        </Panel>
        <Panel scroll>
          <SimpleTabSections key={key} activeTabIndex={activeTabIndex} sections={tabSections} />
        </Panel>
        <PanelBreak height={spacing.large} />
        <SnackbarView open={!!snackbarMessage} error={error} onClose={() => setSnackbarMessage('')}>
          {snackbarMessage}
        </SnackbarView>
      </Panels>
    </ChannelsContext.Provider>
  );
};
